import { shallowRef } from 'vue'
import type { ToastRecord } from './toast-types'

type ToastId = number | string

const EXIT_MS = 200

const MAX_TOASTS = 20

const toasts = shallowRef<ToastRecord[]>([])

let counter = 0

const removalTimers = new Map<ToastId, ReturnType<typeof setTimeout>>()

export function useToasts() {
  return toasts
}

export function create(input: Partial<ToastRecord>): ToastId {
  const id = input.id ?? ++counter
  const existingIndex = toasts.value.findIndex((t) => t.id === id)

  if (existingIndex >= 0) {
    const existing = toasts.value[existingIndex]!
    const pending = removalTimers.get(id)
    if (pending) {
      clearTimeout(pending)
      removalTimers.delete(id)
    }
    const merged: ToastRecord = { ...existing, ...input, id, dismissed: false }
    const next = toasts.value.slice()
    next[existingIndex] = merged
    toasts.value = next
    return id
  }

  const record: ToastRecord = {
    title: '',
    type: 'default',
    ...input,
    id,
    createdAt: Date.now(),
    dismissed: false,
  }
  let next = [record, ...toasts.value]

  if (next.length > MAX_TOASTS) {
    let evictIndex = -1
    for (let i = next.length - 1; i >= 0; i--) {
      if (next[i]!.type !== 'loading') {
        evictIndex = i
        break
      }
    }
    if (evictIndex < 0) evictIndex = next.length - 1
    const evicted = next[evictIndex]!
    const pendingRemoval = removalTimers.get(evicted.id)
    if (pendingRemoval) {
      clearTimeout(pendingRemoval)
      removalTimers.delete(evicted.id)
    }
    next = next.filter((_, i) => i !== evictIndex)
  }

  toasts.value = next
  return id
}

export function dismiss(id?: ToastId): void {
  if (id === undefined) {
    for (const existingId of toasts.value.map((t) => t.id)) dismiss(existingId)
    return
  }

  const index = toasts.value.findIndex((t) => t.id === id)
  if (index < 0) return
  const current = toasts.value[index]!
  if (current.dismissed) return

  const marked: ToastRecord = { ...current, dismissed: true }
  const next = toasts.value.slice()
  next[index] = marked
  toasts.value = next

  marked.onDismiss?.(marked)

  const timer = setTimeout(() => remove(id), EXIT_MS)
  removalTimers.set(id, timer)
}

function remove(id: ToastId): void {
  removalTimers.delete(id)
  toasts.value = toasts.value.filter((t) => t.id !== id)
}
